import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    Box
} from '@mui/material';
import { styled } from '@mui/system';
import { useAuth } from './context/AuthContext';

// --- ROTATING BACKGROUND VIDEOS ---
const BACKGROUND_VIDEOS = [
    { src: 'https://dto86tiwj6qnu.cloudfront.net/yoga_on_the_lawn.mp4', startTime: 0, endTime: null },
    { src: 'https://dto86tiwj6qnu.cloudfront.net/cycling_in_the_street.mp4', startTime: 0, endTime: null },
    { src: 'https://www-media.marsreel.com/wp-content/uploads/2019/03/19235924/MARS-REEL_SPLASH_V04C.mp4', startTime: 15, endTime: 26 },
    { src: 'https://dto86tiwj6qnu.cloudfront.net/film_screening.mp4', startTime: 0, endTime: null },
    { src: 'https://www-media.marsreel.com/wp-content/uploads/2019/03/19235924/MARS-REEL_SPLASH_V04C.mp4', startTime: 6, endTime: 12 },
];

// --- STYLES ---
const Root = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '100vh',
    textAlign: 'center',
    color: '#fff',
    paddingTop: '4rem',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: 'transparent',
});

const BackgroundWrapper = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    zIndex: -1,
});

const BackgroundVideo = styled('video')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
});

const Overlay = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 0,
});

const Content = styled(Box)({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
});

const CTAButton = styled(Button)({
    backgroundColor: '#C63D57',
    color: '#fff',
    fontSize: '1.4rem',
    padding: '14px 0',
    minWidth: '250px',
    width: '100%',
    maxWidth: '300px',
    borderRadius: '6px',
    marginTop: '2rem',
    '&:hover': { backgroundColor: '#a83348' },
});

const Footer = styled(Box)({
    width: '100%',
    padding: '2rem 0',
    textAlign: 'center',
    fontSize: '0.9rem',
    color: '#b0b0b0',
    backgroundColor: 'transparent',
    zIndex: 2,
});

// --- COMPONENT ---
const LandingPage = () => {
    const navigate = useNavigate();
    const { loading } = useAuth();
    const videoRef = useRef(null);

    // Random starting video index - computed once on mount
    const initialVideoIndex = useMemo(() => Math.floor(Math.random() * BACKGROUND_VIDEOS.length), []);

    // 🎥 Video state - starts with random index
    const [currentVideo, setCurrentVideo] = useState(initialVideoIndex);

    // Move to the next video
    const goToNextVideo = useCallback(() => {
        setCurrentVideo((prev) => (prev + 1) % BACKGROUND_VIDEOS.length);
    }, []);

    // When video ends naturally, move to the next one
    const handleVideoEnded = useCallback(() => {
        goToNextVideo();
    }, [goToNextVideo]);

    // When video metadata loads, seek to start time if specified
    const handleLoadedMetadata = useCallback(() => {
        const video = videoRef.current;
        const startTime = BACKGROUND_VIDEOS[currentVideo].startTime;
        if (video && startTime > 0) {
            video.currentTime = startTime;
        }
    }, [currentVideo]);

    // Check for endTime during playback
    useEffect(() => {
        const video = videoRef.current;
        const endTime = BACKGROUND_VIDEOS[currentVideo].endTime;

        if (!video || !endTime) return;

        const handleTimeUpdate = () => {
            if (video.currentTime >= endTime) {
                goToNextVideo();
            }
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        return () => video.removeEventListener('timeupdate', handleTimeUpdate);
    }, [currentVideo, goToNextVideo]);

    if (loading) return null;

    return (
        <Root>
            <BackgroundWrapper>
                <BackgroundVideo
                    ref={videoRef}
                    key={currentVideo}
                    autoPlay
                    muted
                    playsInline
                    onEnded={handleVideoEnded}
                    onLoadedMetadata={handleLoadedMetadata}
                >
                    <source src={BACKGROUND_VIDEOS[currentVideo].src} type="video/mp4" />
                </BackgroundVideo>
                <Overlay />
            </BackgroundWrapper>

            <Content>
                {/* UNITED AI LOGO */}
                <img
                    src="https://d3dbfxrvl5y81z.cloudfront.net/unitedai.png"
                    alt="United AI Logo"
                    style={{
                        width: '100px',
                        marginBottom: '1.5rem',
                        filter: 'drop-shadow(0px 0px 10px rgba(0,0,0,0.4))',
                        opacity: 0.95,
                    }}
                />

                {/* HEADLINE */}
                <Typography variant="h5" sx={{ fontWeight: 300, mb: 3 }}>
                    Artificial Intelligence that brings people together.
                </Typography>

                {/* CTA BUTTON */}
                <CTAButton onClick={() => navigate('/choose-plan')}>
                    Get Started
                </CTAButton>
            </Content>

            <Footer>
                <Typography variant="body2">
                    © 2025 United Artificial Intelligence Company, Inc. All rights reserved.
                </Typography>
            </Footer>
        </Root>
    );
};

export default LandingPage;